import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Button, Empty, Spin, Tabs, Tag, message } from 'antd';

import { ArrowLeftOutlined } from '@ant-design/icons';

import { api } from '../../http/API';
import { monitorAPI } from '../../http/MonitorAPI';
import { FieldCropHistory } from '../../models/FieldCropHistory';
import { FieldState } from '../../models/FieldState';
import { FieldSummary } from '../../models/FieldsSummary';

import FieldStateTab from './tabs/FieldStateTab';
import FieldSeasonsTab from './tabs/FieldSeasonsTab';
import FieldReportTab from './tabs/FieldReportTab';
import FieldMiniMap from './components/FieldMiniMap';

const STATUS_META: Record<string, { color: string; label: string }> = {
    ok: { color: 'green', label: 'норма' },
    warn: { color: 'orange', label: 'внимание' },
    none: { color: 'default', label: 'нет данных' },
};

interface Props {
    setPageTitle?: (title: string) => void;
}

const FieldPage = ({ setPageTitle }: Props) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const fieldId = Number(id);

    const [state, setState] = useState<FieldState | null>(null);
    const [summary, setSummary] = useState<FieldSummary | null>(null);
    const [seasons, setSeasons] = useState<FieldCropHistory[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>('');

    const loadAll = () => {
        if (!fieldId) return;
        setLoading(true);
        setLoadError('');

        Promise.all([
            monitorAPI.GetFieldState(fieldId),
            monitorAPI.GetFieldsSummary().catch(() => [] as FieldSummary[]),
            api.GetFieldSeasons(fieldId).catch(() => [] as FieldCropHistory[]),
        ])
            .then(([s, allSummary, ss]) => {
                setState(s);
                setSummary((allSummary || []).find((f) => f.id === fieldId) || null);
                setSeasons(ss || []);
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Не удалось загрузить данные поля.';
                setLoadError(msg);
                message.error(msg);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAll();
    }, [fieldId]);

    useEffect(() => {
        if (state) {
            setPageTitle?.(state.field.name || `Поле #${state.field.id}`);
        }
        return () => setPageTitle?.('');
    }, [state, setPageTitle]);

    if (loading) {
        return (
            <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (loadError || !state) {
        return (
            <div>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/settings/fields')} style={{ marginBottom: 16 }}>
                    К списку полей
                </Button>
                <Empty description={loadError || 'Поле не найдено'} />
            </div>
        );
    }

    const area = state.field.area_ga ?? summary?.area_ga;
    const cultureName = summary?.season?.culture_name;
    const phase = summary?.season?.current_phase;
    const status = STATUS_META[summary?.status || 'none'] || STATUS_META.none;

    return (
        <div>

            <div className="field-meta">
                <div className="field-meta-item">
                    <span className="field-meta-label">Площадь</span>
                    <span>{area ? `${area.toFixed(1)} га` : '—'}</span>
                </div>
                <div className="field-meta-item">
                    <span className="field-meta-label">Культура</span>
                    <span>{cultureName || '—'}</span>
                </div>
                <div className="field-meta-item">
                    <span className="field-meta-label">Фаза</span>
                    <span>{phase || '—'}</span>
                </div>
                <div className="field-meta-item">
                    <span className="field-meta-label">Статус</span>
                    <Tag color={status.color} style={{ margin: 0 }}>{status.label}</Tag>
                </div>
            </div>

            {summary && summary.geo_json ? (
                <div style={{ marginBottom: 16 }}>
                    <FieldMiniMap geoJson={summary.geo_json} />
                </div>
            ) : null}

            <Tabs
                defaultActiveKey="state"
                items={[
                    {
                        key: 'state',
                        label: 'Сейчас',
                        children: <FieldStateTab state={state} loading={false} summary={summary} stationNumber={summary?.station?.number_in_db} />,
                    },
                    {
                        key: 'seasons',
                        label: `Сезоны (${seasons.length})`,
                        children: (
                            <FieldSeasonsTab
                                fieldId={fieldId}
                                seasons={seasons}
                                cultures={[]}
                                onReload={loadAll}
                            />
                        ),
                    },
                    {
                        key: 'report',
                        label: 'Отчёт',
                        children: <FieldReportTab fieldId={fieldId} seasons={seasons} />,
                    },
                ]}
            />
        </div>
    );
};

export default FieldPage;
